import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Select, Tooltip, TTableRow } from '@treezor/design-system';

import { DashboardUser2FAStatus } from '@/features/dashboard-users/components/DashboardUser2FAStatus';
import { DashboardUserActions } from '@/features/dashboard-users/components/DashboardUserActions';
import { ISupportUserConfirmModalProps } from '@/features/dashboard-users/components/SupportUserConfirmModal';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { useAllAdvancedRoles } from '@/features/roles/hooks/useAdvancedRole';
import {
  advancedRoleStatus,
  roleSlugValues,
  TAdvancedRoleSlug,
} from '@/features/roles/store/models/advancedRole.model';
import { EScope } from '@/features/roles/store/models/role.model';
import {
  dashboardUserHeadersList,
  ISupportUser,
  TDashboardUsersRowType,
} from '@/features/dashboard-users/store/support-user.model';
import { getDuration, getMonth } from '@/utils/tools/DateTools';
import { zeroPad } from '@/utils/tools/MathTools';
import { RoleOption, RoleSelectedOption } from '@/features/dashboard-users/components/RoleOption';
import { openModal } from '@/store/actions/core.actions';
import { getSupportUserScopes } from '@/features/roles/helpers/getSupportUserScopes';
import { DashboardUserStatus } from '@/features/dashboard-users/components/DashboardUserStatus/DashboardUserStatus';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';
import { PER_PAGE_USERS } from '@/features/dashboard-users/components/DashboardUsers';

export function useDashboardUserTableData(
  dashboardUsers: ISupportUser[],
  shouldDisplayReviewNotice: boolean,
  currentSupportUserId: string,
  isLoading = false,
  isActiveDashboardUserDisplayed = true,
  onManageDashboardUser: (dashboardUser: ISupportUser) => void,
) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectSavedValue = useRef<ISupportUser | undefined>(undefined);
  const [currentConfirmUser, setCurrentConfirmUser] = useState<string | null>(null);

  const {
    role: currentDashboardUserAdvancedRole,
    advandeRoleFromScopes,
    advandeRoleFromSlug,
    config: advancesRolesConfig,
  } = useAccesControl();

  const { isLoading: isLoadingAdvancedRoles } = useAllAdvancedRoles();

  const onAdvancedRoleConfirmClose = useCallback(() => {
    setCurrentConfirmUser(null);
  }, [setCurrentConfirmUser]);

  const onAdvancedRoleChange = useCallback(
    (newRoleSlug: TAdvancedRoleSlug, user: ISupportUser) => {
      const newRole = advancesRolesConfig.find((role) => newRoleSlug === role.slug);
      const scopes = getSupportUserScopes(user);
      const oldRole = user.metadata?.role?.slug
        ? advancesRolesConfig.find((role) => user.metadata.role.slug === role.slug)
        : advandeRoleFromScopes(scopes);
      if (newRole.slug !== oldRole.slug) {
        setCurrentConfirmUser(user.id);
        selectSavedValue.current = user;
        dispatch(
          openModal<ISupportUserConfirmModalProps>('supportUserConfirmModal', {
            user,
            oldRole,
            newRole,
            onClose: onAdvancedRoleConfirmClose,
            onSubmit: onAdvancedRoleConfirmClose,
          }),
        );
      }
    },
    [dispatch, advancesRolesConfig, advandeRoleFromScopes, onAdvancedRoleConfirmClose],
  );

  if (isLoading) {
    return RowSkeleton<TDashboardUsersRowType>({ headers: dashboardUserHeadersList, length: PER_PAGE_USERS });
  }

  return dashboardUsers?.map((dashboardUser, index): TTableRow<TDashboardUsersRowType> => {
    const scopes = getSupportUserScopes(dashboardUser);
    const uniqueScopes: EScope[] = [...new Set(scopes)] as EScope[];
    const isBlocked = dashboardUser.wrongPwdAttempts >= 3;
    const client = dashboardUser.clients[dashboardUser.clients.length - 1];
    const lastLogin = getDuration(new Date(client.lastLoginAt));
    const adhesionDate = new Date(client.acceptedAt);
    const suspensionDate = new Date(dashboardUser.statusUpdatedAt);
    const dashboardUserRole =
      dashboardUser.metadata?.role?.status === advancedRoleStatus.VALIDATED
        ? advandeRoleFromSlug(dashboardUser.metadata.role.slug)
        : advandeRoleFromScopes(uniqueScopes);
    const isEditionDisable =
      dashboardUserRole.slug === roleSlugValues.ADMINISTRATOR &&
      currentDashboardUserAdvancedRole?.slug !== roleSlugValues.ADMINISTRATOR;

    const reviewStatus =
      shouldDisplayReviewNotice && dashboardUser?.metadata?.role?.status !== advancedRoleStatus.VALIDATED ? (
        <div className="w-full flex items-end">
          <Tooltip
            contentClassName="max-w-12"
            messageContent={t(`pages.administration.supportsUsers.tooltip.recentlyUpdatedRole`)}
            placement="top"
            variant="primary"
          >
            <div className="bg-icon-info w-3 h-3 rounded-full"></div>
          </Tooltip>
        </div>
      ) : null;

    const role =
      isLoadingAdvancedRoles || currentConfirmUser === dashboardUser.id ? (
        <></>
      ) : isActiveDashboardUserDisplayed ? (
        <Select
          size="s"
          options={
            currentDashboardUserAdvancedRole.slug !== roleSlugValues.ADMINISTRATOR &&
            dashboardUserRole?.slug !== roleSlugValues.ADMINISTRATOR
              ? advancesRolesConfig.filter((role) => role.slug !== roleSlugValues.ADMINISTRATOR)
              : advancesRolesConfig
          }
          getOptionLabel={RoleOption}
          getOptionDisplay={RoleSelectedOption}
          getOptionValue={(role) => role.slug}
          defaultValue={dashboardUserRole?.slug}
          disabled={isEditionDisable}
          onChange={(roleSlug: TAdvancedRoleSlug) => onAdvancedRoleChange(roleSlug, dashboardUser)}
        ></Select>
      ) : (
        <span>{dashboardUserRole.name}</span>
      );

    const status = (
      <DashboardUserStatus
        isBlocked={isBlocked}
        dashboardUser={dashboardUser}
        rowIndex={index}
        userId={dashboardUser.id}
      />
    );

    const suspensionDateValue = suspensionDate ? (
      <span className="typo-body-regular-small">{`${t(getMonth(suspensionDate.getMonth())).slice(0, 3)} ${zeroPad(suspensionDate.getDate(), 2)}, ${suspensionDate.getFullYear()}`}</span>
    ) : (
      <span></span>
    );

    const twofa = <DashboardUser2FAStatus dashboardUser={dashboardUser} isFirstRow={index === 0} />;

    const lastactive = (
      <span className="typo-body-regular-small text-center">
        {!!client.lastLoginAt ? `${lastLogin.value} ${lastLogin.unit} ago` : '-'}
      </span>
    );

    const adhesion = (
      <span className="typo-body-regular-small">{`${t(getMonth(adhesionDate.getMonth())).slice(0, 3)} ${zeroPad(adhesionDate.getDate(), 2)}, ${adhesionDate.getFullYear()}`}</span>
    );

    const actions = isEditionDisable ? (
      <></>
    ) : (
      <DashboardUserActions
        isOwnAccount={currentSupportUserId === dashboardUser.id}
        supportUser={dashboardUser}
        currentSupportUserRole={currentDashboardUserAdvancedRole}
        onManageDashboardUser={() => onManageDashboardUser(dashboardUser)}
        isActiveDashboardUserDisplayed={isActiveDashboardUserDisplayed}
      />
    );

    return {
      reviewStatus,
      username: <span className="typo-body-regular-paragraph">{dashboardUser.username}</span>,
      role,
      status,
      suspensionDate: suspensionDateValue,
      '2fa': twofa,
      lastactive,
      adhesion,
      actions,
    };
  });
}
